"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
  Search, MapPin, Briefcase, Clock, ChevronRight, SlidersHorizontal,
  Globe, Mail, Phone, Building2, ExternalLink,
  ChevronLeft, X as XIcon, Check,
} from "lucide-react";

// Brand icons — lucide-react no incluye brand icons (por trademark).
// Definimos los SVGs inline. Cada uno recibe `size` para consistencia
// con el resto de la app. Todos usan fill=currentColor para heredar el
// color del texto del contenedor.
type BrandIconProps = { size?: number; className?: string };
function LinkedinIcon({ size = 15, className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden>
      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
    </svg>
  );
}
function InstagramIcon({ size = 15, className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  );
}
function FacebookIcon({ size = 15, className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden>
      <path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0022 12z"/>
    </svg>
  );
}
function YoutubeIcon({ size = 15, className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden>
      <path d="M23 12s0-3.68-.47-5.45a2.79 2.79 0 00-1.97-1.98C18.79 4.1 12 4.1 12 4.1s-6.79 0-8.56.47A2.79 2.79 0 001.47 6.55C1 8.32 1 12 1 12s0 3.68.47 5.45a2.79 2.79 0 001.97 1.98c1.77.47 8.56.47 8.56.47s6.79 0 8.56-.47a2.79 2.79 0 001.97-1.98C23 15.68 23 12 23 12zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
    </svg>
  );
}
function XIcon2({ size = 15, className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2H21.5l-7.53 8.606L22 22h-6.797l-5.32-6.96L3.8 22H.542l8.06-9.207L2 2h6.968l4.814 6.363L18.244 2zm-1.19 18h1.833L7.03 3.888H5.06L17.054 20z"/>
    </svg>
  );
}
import Link from "next/link";
import Image from "next/image";

type SortKey = "recent" | "title" | "salary_desc";

const PAGE_SIZE = 12;

type Company = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  banner_url: string | null;
  primary_color: string;
  accent_color: string;
  portal_headline: string | null;
  portal_description: string | null;
  portal_text_color: string | null;
  website: string | null;
  portal_phone: string | null;
  portal_address: string | null;
  portal_contact_email: string | null;
  portal_about: string | null;
  portal_benefits: string | null;
  portal_linkedin_url: string | null;
  portal_instagram_url: string | null;
  portal_facebook_url: string | null;
  portal_x_url: string | null;
  portal_youtube_url: string | null;
};

type Job = {
  id: string;
  title: string;
  department: string | null;
  location: string | null;
  modality: string | null;
  employment_type: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  salary_visible: boolean;
  published_at: string | null;
  created_at: string;
};

/** Calcula días entre dos fechas comparando días calendario (sin importar la hora) */
function calendarDaysDiff(dateStr: string): number {
  const pub = new Date(dateStr);
  const now = new Date();
  // Strip time — compare midnight of each day
  const pubMidnight = Date.UTC(pub.getUTCFullYear(), pub.getUTCMonth(), pub.getUTCDate());
  const nowMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.floor((nowMidnight - pubMidnight) / 86400000);
}

function timeAgo(dateStr: string): string {
  const days = calendarDaysDiff(dateStr);
  if (days === 0) return "Hoy";
  if (days === 1) return "Ayer";
  if (days < 7) return `Hace ${days} días`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
  return `Hace ${Math.floor(days / 30)} mes${Math.floor(days / 30) > 1 ? "es" : ""}`;
}

function isNew(dateStr: string): boolean {
  return calendarDaysDiff(dateStr) <= 3;
}

export function PortalJobs({
  company,
  jobs,
  talentPoolEnabled = false,
}: {
  company: Company;
  jobs: Job[];
  /** Si la empresa tiene el feature on, mostramos un CTA en el sidebar
   *  encima de "Sobre la empresa" para que candidatos sin job que
   *  les encaje dejen igual su CV. */
  talentPoolEnabled?: boolean;
}) {
  // ── Filtros + sort + paginación persistidos en URL ──────────
  // Read-only en el server component parent (page.tsx no lee searchParams),
  // así que los cambios acá NO disparan re-fetch server — solo sincronizan
  // la URL para que sea compartible. router.replace + scroll:false para no
  // saltar el viewport en cada cambio.
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // searchInput = lo que se ve en el <input>; search = valor debounced que
  // dispara el filtro y la URL. Sin debounce, cada keystroke haría
  // router.replace y se sentiría pesado en mobile.
  const [searchInput, setSearchInput] = useState(() => searchParams.get("q") ?? "");
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const [filterModality, setFilterModality] = useState(() => searchParams.get("modality") ?? "");
  const [filterDept, setFilterDept] = useState(() => searchParams.get("dept") ?? "");
  const [filterLocation, setFilterLocation] = useState(() => searchParams.get("location") ?? "");
  const [sort, setSort] = useState<SortKey>(() => {
    const s = searchParams.get("sort");
    return s === "title" || s === "salary_desc" ? s : "recent";
  });
  const [page, setPage] = useState(() => {
    const n = parseInt(searchParams.get("page") ?? "1", 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  // Debounce search input → search (250ms). El input se ve fluido incluso
  // en mobile viejo.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Sync URL cada vez que cambia algún estado navegable. Omitimos valores
  // default para mantener la URL limpia (no queremos `?sort=recent&page=1`
  // en el enlace canónico).
  useEffect(() => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    if (filterModality) p.set("modality", filterModality);
    if (filterDept) p.set("dept", filterDept);
    if (filterLocation) p.set("location", filterLocation);
    if (sort !== "recent") p.set("sort", sort);
    if (page > 1) p.set("page", String(page));
    const qs = p.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [search, filterModality, filterDept, filterLocation, sort, page, pathname, router]);

  // Reset page → 1 cuando cambia cualquier filtro. Se salta el primer render
  // para respetar el ?page=N inicial si vino en la URL (link compartido).
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
  }, [search, filterModality, filterDept, filterLocation, sort]);

  const departments = Array.from(new Set(jobs.map((j) => j.department).filter(Boolean))) as string[];
  const modalities  = Array.from(new Set(jobs.map((j) => j.modality).filter(Boolean))) as string[];
  const locations   = Array.from(new Set(jobs.map((j) => j.location).filter(Boolean))) as string[];

  const filtered = jobs.filter((j) => {
    const matchSearch =
      !search ||
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      (j.department ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (j.location ?? "").toLowerCase().includes(search.toLowerCase());
    const matchModality  = !filterModality  || j.modality  === filterModality;
    const matchDept      = !filterDept      || j.department === filterDept;
    const matchLocation  = !filterLocation  || j.location  === filterLocation;
    return matchSearch && matchModality && matchDept && matchLocation;
  });

  // Sort in-memory. "recent" es el orden con el que vienen del server
  // (published_at desc) — no re-sorteamos, evitamos el work. "title" es
  // localeCompare("es") para respetar acentos. "salary_desc" usa max o
  // min (lo que exista), tratando ausencia como 0 así los que no tienen
  // sueldo declarado quedan al final.
  const sorted = useMemo(() => {
    if (sort === "recent") return filtered;
    const arr = [...filtered];
    if (sort === "title") {
      arr.sort((a, b) => a.title.localeCompare(b.title, "es", { sensitivity: "base" }));
    } else if (sort === "salary_desc") {
      arr.sort((a, b) => {
        const va = a.salary_visible ? (a.salary_max ?? a.salary_min ?? 0) : 0;
        const vb = b.salary_visible ? (b.salary_max ?? b.salary_min ?? 0) : 0;
        return vb - va;
      });
    }
    return arr;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const paginated = sorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

  const hasActiveFilters = !!(search || filterModality || filterDept || filterLocation);
  const activeFilterCount = [filterModality, filterDept, filterLocation].filter(Boolean).length;
  function clearAllFilters() {
    setSearchInput("");
    setSearch("");
    setFilterModality("");
    setFilterDept("");
    setFilterLocation("");
  }

  // Bottom sheet mobile — se abre desde el botón "Filtros" en mobile
  // (sm:hidden). Los chips inline solo se muestran en desktop.
  const [sheetOpen, setSheetOpen] = useState(false);

  // Bloqueamos scroll del body cuando el sheet está abierto para que el
  // touch scroll no traspase al portal detrás.
  useEffect(() => {
    if (!sheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);

  // Cerrar con Escape (a11y — WCAG 2.1.1).
  useEffect(() => {
    if (!sheetOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSheetOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheetOpen]);

  const primary   = company.primary_color   ?? "#1B2A4A";
  const accent    = company.accent_color    ?? "#D94F3D";
  // portal_text_color queda como legacy — el bloque hero nuevo (Opción C)
  // usa var(--pfg) que responde al modo claro/oscuro. Si algún tenant configuró
  // un text-color custom para su banner viejo, ya no se usa en este componente.
  void company.portal_text_color;

  // Check if there's any contact info to show
  const hasContactInfo = !!(
    company.website ||
    company.portal_contact_email ||
    company.portal_phone ||
    company.portal_address
  );
  // Redes sociales agregadas por Fase E. Se muestra el bloque si hay al
  // menos una URL cargada.
  const socialLinks = [
    { url: company.portal_linkedin_url, Icon: LinkedinIcon, label: "LinkedIn" },
    { url: company.portal_instagram_url, Icon: InstagramIcon, label: "Instagram" },
    { url: company.portal_facebook_url, Icon: FacebookIcon, label: "Facebook" },
    { url: company.portal_x_url, Icon: XIcon2, label: "X (Twitter)" },
    { url: company.portal_youtube_url, Icon: YoutubeIcon, label: "YouTube" },
  ].filter((s): s is { url: string; Icon: (p: BrandIconProps) => JSX.Element; label: string } => !!s.url);
  const hasSocial = socialLinks.length > 0;
  const hasAbout = !!company.portal_about;
  const hasBenefits = !!(company.portal_benefits && company.portal_benefits.trim());
  // El sidebar aparece si hay contact info O si el talent pool está habilitado
  // O si hay redes sociales O si hay contenido "sobre nosotros"/beneficios.
  // Todos son motivos para mostrarlo.
  const showSidebar = hasContactInfo || talentPoolEnabled || hasSocial || hasAbout || hasBenefits;

  return (
    <div>
      {/* Hero — Opción C:
          - BANNER puro (la imagen del tenant sin ningún overlay de color).
            Si no hay banner_url, usa el primary_color como fallback sólido.
          - Debajo, un BLOQUE DE TEXTO independiente con logo + headline + search.
            Va en superficie de card (cambia con modo claro/oscuro), así el
            texto siempre queda legible sin importar qué imagen suba el tenant.
      */}
      {company.banner_url ? (
        // Banner con next/image fill — AVIF/WebP + srcset + LCP priority.
        // El banner es lo más grande y visible al cargar el portal (LCP
        // element casi seguro), por eso priority=true evita el fetch tardío.
        <div
          className="relative h-40 sm:h-52 w-full"
          style={{ backgroundColor: primary }}
        >
          <Image
            src={company.banner_url}
            alt={`Banner de ${company.name}`}
            fill
            priority
            sizes="100vw"
            className="object-cover"
          />
        </div>
      ) : (
        <div className="h-24 w-full" style={{ backgroundColor: primary }} />
      )}

      <section className="bg-[var(--pcard)] border-b border-[var(--pborder)] py-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {company.logo_url && (
            <div className="mx-auto mb-5 -mt-16 sm:-mt-20 inline-flex items-center justify-center p-2 bg-[var(--pcard)] rounded-2xl shadow-md border border-[var(--pborder)] h-14">
              <Image
                src={company.logo_url}
                alt={`Logo de ${company.name}`}
                width={160}
                height={56}
                priority
                className="h-full w-auto object-contain"
                sizes="160px"
              />
            </div>
          )}
          <h1 className="font-jakarta text-3xl sm:text-4xl font-bold mb-3 leading-tight text-[var(--pfg)]">
            {company.portal_headline ?? (
              <>Trabajá con nosotros en{" "}<span style={{ color: accent }}>{company.name}</span></>
            )}
          </h1>
          <p className="text-base mb-8 text-[var(--pmuted)]">
            {company.portal_description ?? "Buscamos personas talentosas para sumarse al equipo."}
          </p>

          <div className="relative max-w-xl mx-auto">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--pmuted)]" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por puesto o área..."
              aria-label="Buscar posiciones"
              className="w-full pl-11 pr-10 py-3.5 rounded-xl text-sm text-[var(--pfg)] bg-[var(--pbg)] border border-[var(--pborder)] shadow-sm focus:outline-none focus:ring-2 focus:border-transparent placeholder:text-[var(--psubtle)]"
              style={{ ["--tw-ring-color" as string]: accent }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                aria-label="Limpiar búsqueda"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pmuted)] hover:text-[var(--pfg)] transition-colors"
              >
                <XIcon size={16} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Fase E: Sobre nosotros + Beneficios movidos al sidebar
          (a pedido: entre "Subí tu CV" y el card de contacto). Ver el
          <aside> más abajo. Antes iban full-width bajo el hero pero
          desconectaban visualmente del bloque de "info del tenant". */}

      {/* Main content — jobs + optional sidebar */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className={`flex gap-8 ${showSidebar ? "flex-col lg:flex-row" : ""}`}>
          {/* Jobs column */}
          <div className="flex-1 min-w-0">
            {/* Mobile filter trigger — solo en <sm. Abre bottom sheet. */}
            {(departments.length > 0 || modalities.length > 0 || locations.length > 0) && (
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={sheetOpen}
                className="sm:hidden inline-flex items-center gap-2 h-10 px-4 mb-6 rounded-xl text-sm font-semibold text-[var(--pfg)] bg-[var(--pcard)] border border-[var(--pborder-strong)] hover:bg-[var(--phover)] transition-colors"
              >
                <SlidersHorizontal size={14} />
                Filtros{activeFilterCount > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full text-[10px] font-bold text-white"
                    style={{ backgroundColor: primary }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}

            {/* Desktop filters inline (chips) — solo sm+. */}
            {(departments.length > 0 || modalities.length > 0 || locations.length > 0) && (
              <div className="hidden sm:flex flex-wrap items-center gap-3 mb-8">
                <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--pmuted)]">
                  <SlidersHorizontal size={13} />
                  Filtrar:
                </div>
                {modalities.map((m) => {
                  const on = filterModality === m;
                  return (
                    <button
                      key={m}
                      onClick={() => setFilterModality(on ? "" : m)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize shadow-sm ${on ? "" : "bg-[var(--pcard)] border-[var(--pborder-strong)] text-[var(--pfg)] hover:bg-[var(--phover)]"}`}
                      style={on ? { backgroundColor: primary, color: "#ffffff", borderColor: primary } : undefined}
                    >
                      {m}
                    </button>
                  );
                })}
                {departments.map((d) => {
                  const on = filterDept === d;
                  return (
                    <button
                      key={d}
                      onClick={() => setFilterDept(on ? "" : d)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all shadow-sm ${on ? "" : "bg-[var(--pcard)] border-[var(--pborder-strong)] text-[var(--pfg)] hover:bg-[var(--phover)]"}`}
                      style={on ? { backgroundColor: primary, color: "#ffffff", borderColor: primary } : undefined}
                    >
                      {d}
                    </button>
                  );
                })}
                {locations.map((l) => {
                  const on = filterLocation === l;
                  return (
                    <button
                      key={l}
                      onClick={() => setFilterLocation(on ? "" : l)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all shadow-sm ${on ? "" : "bg-[var(--pcard)] border-[var(--pborder-strong)] text-[var(--pfg)] hover:bg-[var(--phover)]"}`}
                      style={on ? { backgroundColor: primary, color: "#ffffff", borderColor: primary } : undefined}
                    >
                      <MapPin size={10} />
                      {l}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-[var(--pmuted)]">
                  {filtered.length} posición{filtered.length !== 1 ? "es" : ""} encontrada{filtered.length !== 1 ? "s" : ""}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pmuted)] hover:text-[var(--pfg)] transition-colors underline underline-offset-2"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>

              {/* Sort dropdown — solo visible si hay algo que ordenar */}
              {sorted.length > 1 && (
                <label className="flex items-center gap-2 text-xs text-[var(--pmuted)]">
                  Ordenar:
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortKey)}
                    aria-label="Ordenar posiciones"
                    className="text-xs font-semibold text-[var(--pfg)] bg-[var(--pcard)] border border-[var(--pborder)] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2"
                    style={{ ["--tw-ring-color" as string]: accent }}
                  >
                    <option value="recent">Más recientes</option>
                    <option value="title">Título A-Z</option>
                    <option value="salary_desc">Sueldo (mayor a menor)</option>
                  </select>
                </label>
              )}
            </div>

            {sorted.length === 0 ? (
              <div className="text-center py-20">
                <Briefcase size={40} className="mx-auto text-[var(--pborder-strong)] mb-4" strokeWidth={1.5} />
                <h3 className="font-jakarta font-semibold text-[var(--pfg)] mb-2">
                  {hasActiveFilters ? "Sin resultados" : "No hay posiciones abiertas en este momento"}
                </h3>
                <p className="text-sm text-[var(--pmuted)]">
                  {hasActiveFilters
                    ? "Probá con otros filtros o limpiálos para ver todas las posiciones."
                    : "Revisá más adelante, estamos en constante crecimiento."}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {paginated.map((job) => (
                    <JobCard key={job.id} job={job} slug={company.slug} accent={accent} primary={primary} isNew={isNew(job.published_at ?? job.created_at)} />
                  ))}
                </div>

                {/* Paginación — solo si hay >1 página. Prev/next + número
                    de página; los ordinales no se muestran para no clutter
                    el mobile (con >20 páginas terminaba en scroll horizontal). */}
                {totalPages > 1 && (
                  <nav
                    className="flex items-center justify-center gap-2 mt-10"
                    aria-label="Paginación"
                  >
                    <button
                      type="button"
                      disabled={clampedPage === 1}
                      onClick={() => setPage(clampedPage - 1)}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold text-[var(--pfg)] bg-[var(--pcard)] border border-[var(--pborder-strong)] hover:bg-[var(--phover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={14} /> Anterior
                    </button>
                    <span className="text-xs font-medium text-[var(--pmuted)] px-2">
                      Página {clampedPage} de {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={clampedPage === totalPages}
                      onClick={() => setPage(clampedPage + 1)}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold text-[var(--pfg)] bg-[var(--pcard)] border border-[var(--pborder-strong)] hover:bg-[var(--phover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Siguiente <ChevronRight size={14} />
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>

          {/* Company info sidebar — incluye también el CTA del talent pool
              cuando el feature está habilitado. */}
          {showSidebar && (
            <aside className="lg:w-72 flex-shrink-0 space-y-4 lg:sticky lg:top-24 self-start">
              {/* CTA "Subí tu CV a nuestra Base de Datos" — solo el botón,
                  arriba del card "Sobre la empresa". */}
              {talentPoolEnabled && (
                <Link
                  href={`/portal/${company.slug}/cv`}
                  className="inline-flex items-center justify-center w-full px-4 h-11 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                  style={{ background: primary }}
                >
                  Subí tu CV a nuestra Base de Datos
                </Link>
              )}

              {/* Sobre nosotros — arriba del contact card (a pedido). */}
              {hasAbout && (
                <article
                  aria-labelledby="portal-about-title"
                  className="bg-[var(--pcard)] rounded-2xl border border-[var(--pborder)] p-5"
                >
                  <h2
                    id="portal-about-title"
                    className="font-jakarta font-semibold text-sm mb-2"
                    style={{ color: primary }}
                  >
                    Sobre {company.name}
                  </h2>
                  <p className="text-xs text-[var(--pfg)] leading-relaxed whitespace-pre-wrap">
                    {company.portal_about}
                  </p>
                </article>
              )}

              {/* Beneficios — debajo de "sobre" y también arriba del contact.
                  Lista con checks en accent color del tenant. */}
              {hasBenefits && (
                <article
                  aria-labelledby="portal-benefits-title"
                  className="bg-[var(--pcard)] rounded-2xl border border-[var(--pborder)] p-5"
                >
                  <h2
                    id="portal-benefits-title"
                    className="font-jakarta font-semibold text-sm mb-3"
                    style={{ color: primary }}
                  >
                    ¿Qué te ofrecemos?
                  </h2>
                  <ul className="space-y-2">
                    {company.portal_benefits!
                      .split(/\r?\n/)
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-[var(--pfg)]">
                          <span
                            className="flex h-4 w-4 items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: `${accent}20` }}
                            aria-hidden
                          >
                            <Check size={10} style={{ color: accent }} strokeWidth={2.5} />
                          </span>
                          <span className="leading-snug">{benefit}</span>
                        </li>
                      ))}
                  </ul>
                </article>
              )}

              {hasContactInfo && (
              <div className="bg-[var(--pcard)] rounded-2xl border border-[var(--pborder)] p-6">
                {/* Company header */}
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-[var(--pborder)]">
                  {company.logo_url ? (
                    // Sidebar aside no es LCP — sin priority, lazy load default.
                    <Image
                      src={company.logo_url}
                      alt={`Logo de ${company.name}`}
                      width={80}
                      height={40}
                      className="h-10 w-auto object-contain flex-shrink-0 max-w-[80px]"
                      sizes="80px"
                    />
                  ) : (
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: primary }}
                    >
                      {company.name[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-jakarta font-semibold text-[var(--pfg)] text-sm truncate">
                      {company.name}
                    </p>
                    <p className="text-xs text-[var(--pmuted)]">Contactanos</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2563EB]/10 flex-shrink-0 mt-0.5">
                        <Globe size={14} className="text-[#2563EB]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[var(--pmuted)] mb-0.5">Sitio web</p>
                        <p className="text-sm text-[#2563EB] group-hover:underline truncate flex items-center gap-1">
                          {company.website.replace(/^https?:\/\/(www\.)?/, "")}
                          <ExternalLink size={10} className="flex-shrink-0 opacity-60" />
                        </p>
                      </div>
                    </a>
                  )}

                  {company.portal_contact_email && (
                    <a
                      href={`mailto:${company.portal_contact_email}`}
                      className="flex items-start gap-3 group"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7C3AED]/10 flex-shrink-0 mt-0.5">
                        <Mail size={14} className="text-[#7C3AED]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[var(--pmuted)] mb-0.5">Email RRHH</p>
                        <p className="text-sm text-[var(--pfg)] group-hover:text-[#7C3AED] transition-colors truncate">
                          {company.portal_contact_email}
                        </p>
                      </div>
                    </a>
                  )}

                  {company.portal_phone && (
                    <a
                      href={`tel:${company.portal_phone}`}
                      className="flex items-start gap-3 group"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0EA5E9]/10 flex-shrink-0 mt-0.5">
                        <Phone size={14} className="text-[#0EA5E9]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[var(--pmuted)] mb-0.5">Teléfono</p>
                        <p className="text-sm text-[var(--pfg)] group-hover:text-[#0EA5E9] transition-colors">
                          {company.portal_phone}
                        </p>
                      </div>
                    </a>
                  )}

                  {company.portal_address && (
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--pmuted)]/10 flex-shrink-0 mt-0.5">
                        <Building2 size={14} className="text-[var(--pmuted)]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[var(--pmuted)] mb-0.5">Dirección</p>
                        <p className="text-sm text-[var(--pfg)] leading-snug">
                          {company.portal_address}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Redes sociales — bloque compacto con iconos clickeables.
                  Cada link abre en nueva pestaña. rel="noopener" para
                  seguridad (evita window.opener hijack). */}
              {hasSocial && (
                <div className="bg-[var(--pcard)] rounded-2xl border border-[var(--pborder)] p-5">
                  <p className="text-xs font-semibold text-[var(--pmuted)] uppercase tracking-wide mb-3">
                    Seguinos en
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {socialLinks.map(({ url, Icon, label }) => (
                      <a
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        title={label}
                        className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--pborder)] text-[var(--pmuted)] hover:text-white transition-all"
                        style={{ ["--tw-hover-bg" as string]: primary }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = primary; (e.currentTarget as HTMLElement).style.borderColor = primary; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ""; (e.currentTarget as HTMLElement).style.borderColor = ""; }}
                      >
                        <Icon size={15} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          )}
        </div>
      </div>

      {/* Bottom sheet — mobile only. Se renderiza siempre en el DOM
          (con display:none via sm:hidden) pero solo se muestra visualmente
          si sheetOpen=true. role=dialog + aria-modal cierran el patrón. */}
      {sheetOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="portal-filters-title"
          className="fixed inset-0 z-50 sm:hidden"
        >
          {/* Backdrop — click cierra */}
          <button
            type="button"
            aria-label="Cerrar filtros"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-black/50 animate-in fade-in duration-150"
          />
          {/* Panel — pega al bottom, animate slide-up */}
          <div className="absolute inset-x-0 bottom-0 bg-[var(--pcard)] rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl animate-in slide-in-from-bottom duration-200">
            {/* Handle visual (drag affordance) */}
            <div className="pt-2 pb-1 flex justify-center flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-[var(--pborder-strong)]" />
            </div>
            <div className="px-5 pb-3 flex items-center justify-between flex-shrink-0">
              <h2 id="portal-filters-title" className="font-jakarta font-semibold text-[var(--pfg)] text-base">
                Filtros
              </h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Cerrar filtros"
                className="h-9 w-9 flex items-center justify-center rounded-lg text-[var(--pmuted)] hover:text-[var(--pfg)] hover:bg-[var(--phover)] transition-colors"
              >
                <XIcon size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-6">
              {modalities.length > 0 && (
                <FilterGroup title="Modalidad">
                  {modalities.map((m) => (
                    <SheetChip
                      key={m}
                      active={filterModality === m}
                      onClick={() => setFilterModality(filterModality === m ? "" : m)}
                      primary={primary}
                    >
                      {m}
                    </SheetChip>
                  ))}
                </FilterGroup>
              )}
              {departments.length > 0 && (
                <FilterGroup title="Departamento">
                  {departments.map((d) => (
                    <SheetChip
                      key={d}
                      active={filterDept === d}
                      onClick={() => setFilterDept(filterDept === d ? "" : d)}
                      primary={primary}
                    >
                      {d}
                    </SheetChip>
                  ))}
                </FilterGroup>
              )}
              {locations.length > 0 && (
                <FilterGroup title="Ubicación">
                  {locations.map((l) => (
                    <SheetChip
                      key={l}
                      active={filterLocation === l}
                      onClick={() => setFilterLocation(filterLocation === l ? "" : l)}
                      primary={primary}
                    >
                      <MapPin size={11} className="inline mr-1" />
                      {l}
                    </SheetChip>
                  ))}
                </FilterGroup>
              )}
            </div>

            <div className="flex gap-3 p-4 border-t border-[var(--pborder)] flex-shrink-0">
              <button
                type="button"
                onClick={clearAllFilters}
                disabled={!hasActiveFilters}
                className="flex-1 h-11 rounded-xl text-sm font-semibold text-[var(--pfg)] border border-[var(--pborder-strong)] bg-[var(--pcard)] hover:bg-[var(--phover)] disabled:opacity-40 transition-colors"
              >
                Limpiar
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 h-11 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: primary }}
              >
                Ver {sorted.length} posición{sorted.length !== 1 ? "es" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helpers del bottom sheet — bloque de grupo de chips + chip individual.
function FilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-[var(--pmuted)] uppercase tracking-wide mb-2">
        {title}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function SheetChip({
  active,
  onClick,
  primary,
  children,
}: {
  active: boolean;
  onClick: () => void;
  primary: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all ${
        active
          ? ""
          : "bg-[var(--pcard)] border-[var(--pborder-strong)] text-[var(--pfg)]"
      }`}
      style={active ? { backgroundColor: primary, color: "#ffffff", borderColor: primary } : undefined}
    >
      {children}
    </button>
  );
}

function JobCard({
  job,
  slug,
  accent,
  primary,
  isNew,
}: {
  job: Job;
  slug: string;
  accent: string;
  primary: string;
  isNew: boolean;
}) {
  const tags = [job.modality, job.employment_type].filter(Boolean);
  const date = job.published_at ?? job.created_at;

  return (
    <Link
      href={`/portal/${slug}/jobs/${job.id}`}
      className="group block bg-[var(--pcard)] rounded-2xl border border-[var(--pborder)] p-5 hover:border-transparent hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: `${primary}15` }}
          >
            <Briefcase size={18} style={{ color: primary }} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-jakarta font-semibold text-[var(--pfg)] text-base leading-tight">
                {job.title}
              </h3>
              {isNew && (
                <span
                  className="px-2 py-0.5 text-[10px] font-bold rounded-full tracking-wide uppercase"
                  style={{ backgroundColor: `${accent}18`, color: accent }}
                >
                  Nuevo
                </span>
              )}
            </div>
            {job.department && (
              <p className="text-xs text-[var(--pmuted)] mt-1">{job.department}</p>
            )}
          </div>
        </div>
        <ChevronRight size={16} className="text-[var(--pborder-strong)] group-hover:text-[var(--pmuted)] mt-1 transition-colors flex-shrink-0" />
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-[11px] font-medium rounded-full border border-[var(--pborder)] text-[var(--pmuted)] capitalize"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-1.5 mb-4">
        {job.location && (
          <p className="flex items-center gap-1.5 text-xs text-[var(--pmuted)]">
            <MapPin size={11} />
            {job.location}
          </p>
        )}
        {job.salary_visible && job.salary_min && (
          <p className="text-xs font-medium text-[var(--pfg)]">
            {job.salary_currency} {job.salary_min.toLocaleString()}
            {job.salary_max ? ` – ${job.salary_max.toLocaleString()}` : "+"}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-[var(--pborder)]">
        <span className="flex items-center gap-1 text-[10px] text-[var(--pmuted)]">
          <Clock size={10} />
          {timeAgo(date)}
        </span>
        <span className="text-xs font-semibold" style={{ color: accent }}>
          Ver más →
        </span>
      </div>
    </Link>
  );
}
